import { Button } from '@playcastdotio/Shadcn';
import { invitationState, lobbyState, ruleState } from '../../../api/stores/lobbyStores';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { connectPeer } from '../../../api/modules/player';
import { BrainrotAutoJoiningState, BrainrotNoMachinesState, isBrainrotMode } from './Deck';

type Status = 'idle' | 'pending' | 'accepted' | 'rejected';

export const Queue = () => {
  // const queueStatus = ['This queue is first come first serve', 'This queue prioritizes new players', 'you have 30 seconds to join'];
  const [knockStatus, setKnockStatus] = useState<Status>('pending');
  const [joinButtonEnabled, setJoinButtonEnabled] = useState(false);
  const [queuePosition, setQueuePosition] = useState<number | undefined>(undefined);
  const [queuePositionText, setQueuePositionText] = useState('at an unknown position');
  const [showQueuePosition, setShowQueuePosition] = useState(false);
  const [queueDescription, setQueueDescription] = useState('');
  const [isRejected, setIsRejected] = useState(false);

  // Brainrot mode state
  const [inBrainrotMode, setInBrainrotMode] = useState(false);
  const [showBrainrotNoMachines, setShowBrainrotNoMachines] = useState(false);

  useEffect(() => {
    const lobbyListener = lobbyState.addListener((state) => {
      console.log('Lobby state:', state);
      if (state.accepted) {
        setJoinButtonEnabled(true);
        setKnockStatus('accepted');
        if (ruleState.currentState.autoJoinFromQueue) {
          connectPeer(lobbyState.currentState.broadcastHostId);
        }
      }
      if (state.rejected) {
        setIsRejected(true);
      }
      if (typeof state.position === 'number') {
        setQueuePosition(state.position);
      }
    });
    const invitationListener = invitationState.addListener((state) => {
      setQueueDescription(state.selectedInvite?.description || '');
    });
    if (typeof lobbyState.currentState.position === 'number') {
      setQueuePosition(lobbyState.currentState.position);
    }
    setQueueDescription(invitationState.currentState.selectedInvite?.description || '');
    return () => {
      lobbyState.removeListener(lobbyListener);
      invitationState.removeListener(invitationListener);
    };
  }, []);

  useEffect(() => {
    if (!queuePosition) {
      return;
    }
    // Some backend paths use 0 for "next to play"; normalize for UI ordinals.
    const normalizedQueuePosition = queuePosition <= 0 ? 1 : queuePosition;

    if (normalizedQueuePosition === 1) {
      setQueuePositionText('1st');
    } else if (normalizedQueuePosition === 2) {
      setQueuePositionText('2nd');
    } else if (normalizedQueuePosition === 3) {
      setQueuePositionText('3rd');
    } else {
      setQueuePositionText(`${normalizedQueuePosition}th`);
    }
    setShowQueuePosition(true);
  }, [queuePosition]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowQueuePosition(true);
    }, 2000);
    console.log('Timer started');
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    // Check if we're in brainrot mode
    const brainrotMode = isBrainrotMode();
    setInBrainrotMode(brainrotMode);

    if (brainrotMode) {
      // For brainrot mode, we start in auto-joining state
      // The "no machines" state would be set by parent logic if needed
      setShowBrainrotNoMachines(false);
    }
  }, []);

  const handleContinue = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    console.log('Continue');
  };

  const handleJoinNow = () => {
    console.log('Join Now');
    // exchangeKnock();
  };

  return (
    <div className='bg-hero bg-content text-content-text m-0 h-screen w-full bg-cover bg-no-repeat p-0'>
      <div className='container mx-auto flex h-auto w-full flex-col items-center p-8'>
        <section id='hero' className='grid grid-cols-12 gap-x-4 gap-y-8'>
          <div className='col-span-full flex flex-grow flex-row items-center justify-center gap-x-2 pt-3 align-middle'>
            <img src='/images/playcast_logo-96x96.png' alt='Logo' className='h-12 w-12' />
            <h1 className='text-content-text-headline font-headline text-3xl font-bold md:pt-2'>
              <Link to='/'>Playcast</Link>
            </h1>
          </div>
        </section>
        {!isRejected ? (
          ruleState.currentState.autoJoinFromQueue && knockStatus === 'accepted' ? (
            <span></span>
          ) : inBrainrotMode ? (
            // Show brainrot components when in brainrot mode
            showBrainrotNoMachines ? (
              <BrainrotNoMachinesState />
            ) : (
              <BrainrotAutoJoiningState />
            )
          ) : (
            <div className='mt-12 flex w-full justify-center'>
              <div className='bg-footer flex w-full flex-col items-center space-y-4 rounded-lg p-8 shadow-md sm:w-2/3 md:w-3/4 lg:w-2/6'>
                <h2 className='row-auto -mt-4 text-2xl font-bold lg:text-3xl'>
                  {invitationState.currentState.selectedInvite?.description}
                </h2>
                <h3 className='row-auto text-center text-xl font-semibold'>
                  {showQueuePosition ? (knockStatus === 'accepted' ? "It's all you!" : `You are ${queuePositionText} in lobby`) : ''}
                </h3>
                {!ruleState.currentState.autoJoinFromQueue && (
                  <Button disabled={!joinButtonEnabled} className='w-fit rounded-full' onClick={handleJoinNow}>
                    Join Now
                  </Button>
                )}
              </div>
            </div>
          )
        ) : (
          <div className='mt-8 flex flex-col items-center gap-14'>
            <div className='bg-footer w-full space-y-4 rounded-lg p-4 py-6 text-center shadow-md md:w-2/3 lg:w-2/5'>
              <h3 className='row-auto font-[Syne] text-2xl font-bold'>Lobby Update</h3>
              <p className='text-md row-auto text-center font-normal'>You have been removed from the lobby</p>
            </div>
            <div className='bg-footer w-full space-y-4 rounded-lg p-4 py-6 text-center shadow-md md:w-2/3 lg:w-2/5'>
              <h3 className='row-auto font-[Syne] text-2xl font-bold'>Want more Playcast?</h3>
              <p className='text-md row-auto text-center font-normal'>Set up your own Playcast now at Playcast.io</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
